import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import {
  Mail,
  MessageSquare,
  Eye,
  EyeOff,
  Phone,
  MessageCircle,
  Plus,
  Pencil,
  Trash2,
  X,
  Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { ModalBase } from "@/components/ModalBase/ModalBase";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";

const ICON_OPTIONS = [
  { value: "Mail", label: "E-mail", icon: Mail },
  { value: "MessageSquare", label: "Mensagem", icon: MessageSquare },
  { value: "Eye", label: "Visualizou", icon: Eye },
  { value: "EyeOff", label: "Visualizou e não respondeu", icon: EyeOff },
  { value: "Phone", label: "Telefone", icon: Phone },
  { value: "MessageCircle", label: "Chat", icon: MessageCircle },
];

const COLOR_OPTIONS = [
  { value: "#3B82F6", label: "Azul" },
  { value: "#8B5CF6", label: "Roxo" },
  { value: "#F97316", label: "Laranja" },
  { value: "#EF4444", label: "Vermelho" },
  { value: "#10B981", label: "Verde" },
  { value: "#EC4899", label: "Rosa" },
];

export function getIconComponent(iconName: string, size = 16) {
  const found = ICON_OPTIONS.find((o) => o.value === iconName);
  if (!found) return <MessageSquare size={size} />;
  const IconComp = found.icon;
  return <IconComp size={size} />;
}

interface GerenciarNotasRapidasModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TemplateFormData {
  title: string;
  description: string;
  icon_name: string;
  color: string;
}

export function GerenciarNotasRapidasModal({
  open,
  onOpenChange,
}: GerenciarNotasRapidasModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<TemplateFormData>({
      defaultValues: {
        icon_name: "Mail",
        color: "#3B82F6",
      },
    });

  const selectedIcon = watch("icon_name");
  const selectedColor = watch("color");

  const { data: templatesData, isLoading } = useQuery({
    queryKey: ["notas-templates"],
    queryFn: async () => {
      const res = await ServiceResumoDesk.listNotasTemplates();
      return res?.data ?? null;
    },
    enabled: open,
    staleTime: 1000 * 60 * 2,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["notas-templates"] });

  const openCreate = () => {
    setEditingId(null);
    reset({ title: "", description: "", icon_name: "Mail", color: "#3B82F6" });
    setShowForm(true);
  };

  const openEdit = (template: any) => {
    setEditingId(template.id);
    reset({
      title: template.title,
      description: template.description ?? "",
      icon_name: template.icon_name ?? "Mail",
      color: template.color ?? "#3B82F6",
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    reset();
  };

  const onSubmit = async (payload: TemplateFormData) => {
    try {
      setIsSubmitting(true);
      let res;
      if (editingId) {
        res = await ServiceResumoDesk.updateNotaTemplate(editingId, payload);
      } else {
        res = await ServiceResumoDesk.createNotaTemplate(payload);
      }
      if (res?.status === 200 || res?.status === 201) {
        toast.success(
          editingId
            ? t("resumoDesk.notes.quick_note.edit_template")
            : t("resumoDesk.notes.quick_note.create_template"),
        );
        invalidate();
        cancelForm();
      } else {
        toast.error(t("resumoDesk.notes.quick_note.error"));
      }
    } catch {
      toast.error(t("resumoDesk.notes.quick_note.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id: number) => {
    confirmAlert({
      title: t("common:confirmation"),
      message: t("resumoDesk.notes.quick_note.delete_confirmation"),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            const res = await ServiceResumoDesk.deleteNotaTemplate(id);
            if (res?.status === 200) {
              invalidate();
            } else {
              toast.error(t("resumoDesk.notes.quick_note.error"));
            }
          },
        },
        { label: t("common:cancel"), onClick: () => {} },
      ],
    });
  };

  const templates: any[] = templatesData?.data ?? templatesData ?? [];

  return (
    <ModalBase
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t("resumoDesk.notes.quick_note.manage_title")}
    >
      <div className="bg-white rounded-3xl p-6 flex flex-col gap-4 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings size={18} className="text-azul-400" />
            <h2 className="font-semibold text-[#1E1E1E] text-lg">
              {t("resumoDesk.notes.quick_note.manage_title")}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Template list */}
        <div className="flex flex-col gap-2">
          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-14 rounded-2xl bg-gray-100 animate-pulse"
              />
            ))}

          {!isLoading && templates.length === 0 && !showForm && (
            <p className="text-sm text-gray-400 text-center py-4">
              {t("resumoDesk.notes.quick_note.no_templates")}
            </p>
          )}

          {!isLoading &&
            templates.map((tmpl: any) => (
              <div
                key={tmpl.id}
                className="flex items-center gap-3 bg-[#F5F5F5] rounded-2xl px-4 py-3"
              >
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: tmpl.color ?? "#3B82F6" }}
                >
                  {getIconComponent(tmpl.icon_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1E1E1E] truncate">
                    {tmpl.title}
                  </p>
                  {tmpl.description && (
                    <p className="text-xs text-gray-400 truncate">
                      {tmpl.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(tmpl)}
                    className="p-1.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-azul-400 transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(tmpl.id)}
                    className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
        </div>

        {/* Form create/edit */}
        {showForm ? (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-3 border border-gray-200 rounded-2xl p-4"
          >
            <p className="text-sm font-semibold text-[#1E1E1E]">
              {editingId
                ? t("resumoDesk.notes.quick_note.edit_template")
                : t("resumoDesk.notes.quick_note.create_template")}
            </p>

            <input
              type="text"
              placeholder={t("resumoDesk.notes.quick_note.form_title") + " *"}
              className="bg-[#F5F5F5] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-azul-400/30"
              required
              {...register("title", { required: true })}
            />

            <textarea
              placeholder={t("resumoDesk.notes.quick_note.form_description")}
              className="bg-[#F5F5F5] rounded-xl px-3 py-2 text-sm resize-none h-16 outline-none focus:ring-2 focus:ring-azul-400/30"
              {...register("description")}
            />

            {/* Icon selector */}
            <div>
              <p className="text-xs text-gray-500 mb-1.5">
                {t("resumoDesk.notes.quick_note.form_icon")}
              </p>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map((opt) => {
                  const IconComp = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      title={opt.label}
                      onClick={() => setValue("icon_name", opt.value)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                        selectedIcon === opt.value
                          ? "border-azul-400 bg-azul-400 text-white"
                          : "border-gray-200 bg-gray-100 text-gray-500 hover:border-azul-400"
                      }`}
                    >
                      <IconComp size={16} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color selector */}
            <div>
              <p className="text-xs text-gray-500 mb-1.5">
                {t("resumoDesk.notes.quick_note.form_color")}
              </p>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    title={opt.label}
                    onClick={() => setValue("color", opt.value)}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      selectedColor === opt.value
                        ? "border-gray-800 scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: opt.value }}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="flex items-center gap-2 bg-[#F5F5F5] rounded-xl px-3 py-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0"
                style={{ backgroundColor: selectedColor }}
              >
                {getIconComponent(selectedIcon, 14)}
              </div>
              <p className="text-sm text-gray-600 truncate">
                {watch("title") || t("resumoDesk.notes.quick_note.form_title")}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={cancelForm}
                className="flex-1 py-2 rounded-2xl bg-gray-100 text-gray-500 text-sm hover:bg-gray-200 transition-colors"
              >
                {t("common:cancel")}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-2 rounded-2xl bg-azul-400 text-white text-sm font-medium hover:opacity-80 transition-opacity disabled:opacity-50"
              >
                {isSubmitting ? "..." : t("resumoDesk.notes.quick_note.save")}
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-azul-400 hover:text-azul-400 transition-colors text-sm"
          >
            <Plus size={16} />
            {t("resumoDesk.notes.quick_note.add_new")}
          </button>
        )}
      </div>
    </ModalBase>
  );
}
